import React from "react";
import { useNavigate } from "react-router-dom";
import { Images } from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ContentPanel from "../../../../components/layout/content/ContentPanel";
import ContentPanelHeader from "../../../../components/layout/content/ContentPanelHeader";

import ContentGrid from "../../../../components/grids/content/ContentGrid";
import EmptyState from "../../../../components/feedback/EmptyState";
import StatusAlert from "../../../../components/alerts/StatusAlert";
import Button from "../../../../components/buttons/Button";

import AlbumThumbnailCard from "../../../../components/cards/thumbnails/albums/AlbumThumbnailCard";

import { useCreatorChannelAlbums } from "../hooks/useCreatorChannelAlbums";

import "./ChannelAlbumsView.css";

const ALBUMS_LIMIT = 12;

export default function ChannelAlbumsView({ username }) {
  const navigate = useNavigate();

  const { albums, loading, loadingMore, error, hasMore, loadMore } =
    useCreatorChannelAlbums({
      username,
      albumId: null,
      limit: ALBUMS_LIMIT,
    });

  const albumList = Array.isArray(albums) ? albums : [];
  const hasAlbums = albumList.length > 0;

  const errorMessage = error
    ? typeof error === "string"
      ? error
      : error.message || "Unable to load albums."
    : "";

  const handleAlbumClick = (album) => {
    const albumTag = String(album?.albumTag || "").trim();

    if (!albumTag) return;

    navigate(`/albums/${encodeURIComponent(albumTag)}`);
  };

  const handleLoadMore = async () => {
    if (loading || loadingMore || !hasMore) return;

    await loadMore();
  };

  const renderAlbum = (album) => (
    <AlbumThumbnailCard
      key={album.albumId ?? album.albumTag}
      album={album}
      onClick={() => handleAlbumClick(album)}
    />
  );

  return (
    <ViewContainer>
      <ContentPanel>
        <ContentPanelHeader
          title="Albums"
          subTitle="Explore photo albums shared on this channel."
        />

        {errorMessage && hasAlbums && (
          <div className="channel-albums__alert" role="alert">
            <StatusAlert type="error" message={errorMessage} />
          </div>
        )}

        {loading && !hasAlbums ? (
          <ContentGrid
            items={[]}
            loading
            columns={4}
            tabletColumns={3}
            mobileColumns={2}
            gap={16}
            skeletonCount={ALBUMS_LIMIT}
          />
        ) : !hasAlbums ? (
          <EmptyState
            icon={<Images size={28} />}
            title={errorMessage ? "Unable to load albums" : "No albums yet"}
            description={
              errorMessage ||
              "No public photo albums have been shared on this channel."
            }
          />
        ) : (
          <>
            <ContentGrid
              items={albumList}
              columns={4}
              tabletColumns={3}
              mobileColumns={2}
              gap={16}
              showEmpty={false}
              renderItem={renderAlbum}
            />

            {hasMore && (
              <div className="channel-albums__footer">
                <Button
                  value={loadingMore ? "Loading..." : "Load More"}
                  variant="primary-outlined"
                  disabled={loading || loadingMore}
                  onClick={handleLoadMore}
                />
              </div>
            )}
          </>
        )}
      </ContentPanel>
    </ViewContainer>
  );
}
